"use client";
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Caption, Paragraph, Title } from "@/components/ui/typography";
import { Input } from "@/components/ui/input";
import { ChevronRight, X, Search } from "lucide-react";
import Image from "next/image";

import { useRef, useState, useEffect } from "react";
import { SearchProduct } from "@/app/redux/SearchBar/Search-service";
import { useFetchData } from "@/app/hooks/query";
import useProducts from "@/app/hooks/useProducts";
import { useRouter, useSearchParams } from "next/navigation";
import { useURLParams } from "@/app/hooks/useURLParams";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

import { formatToUSD } from "@/app/utils/utility";
import { useDispatch } from "react-redux";
import { setSelectedProductId } from "@/app/redux/product-management/product-management-slice";
import { Flash } from "@/lib/SystemIcons";

const Product = ({ ele }) => {
  const product = ele?.EcomInventory || {
    selling_price: 0,
    maximum_selling_price: 0,
  };

  return (
    <>
      <div className="flex gap-4 ">
        <div className="bg-midnightIndigo-150 rounded h-[45px] w-[39px] md:w-[64px] md:h-[62px] lg:w-[58px] lg:h-[68px] p-2">
          {" "}
          <Image
            height={60}
            width={50}
            className="h-full w-full object-cover"
            src={ele?.ProductImages[0]?.image_url}
          />
        </div>
        <div className="flex flex-col justify-center max-w-[250px] md:max-w-[305px] text-ellipsis">
          <Caption
            size="xs"
            className="text-midnightIndigo-300 line-clamp-1 leading-[125%]"
          >
            UPC:{ele?.upc}
          </Caption>
          <Paragraph className="wireless-Medium text-midnightIndigo-400  line-clamp-1 md:line-clamp-2 ">
            {ele?.product_name}
          </Paragraph>
          {product?.flash_sale && (
            <div
              className="flex flex-row items-center justify-center gap-1 bg-system-orange rounded"
              style={{ width: "65px" }}
            >
              <Flash className="h-5 w-5 text-system-crystalWhite" />
              <span className="text-system-crystalWhite mt-1">Sale</span>
            </div>
          )}
        </div>
        <div className="ml-auto flex flex-col justify-center">
          {product.maximum_selling_price > product.selling_price && (
            <Caption
              size="xs"
              className="wireless-Medium text-midnightIndigo-300 text-right line-through"
            >
              {formatToUSD(product.flash_sale || product.maximum_selling_price)}
            </Caption>
          )}

          <Paragraph className="text-midnightIndigo-400 text-right">
            {formatToUSD(product.flash_sale || product.selling_price)}
          </Paragraph>
        </div>
      </div>
      <Separator orientation="horizontal" className="my-4" />
    </>
  );
};

export default function SearchBar() {
  const routeParams = useURLParams();
  const [searchShow, setSearchShow] = useState(false);
  const [mobileSearchBar, setMobileSearchBar] = useState(false);
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState(routeParams?.search || "");
  const [focus, setFocus] = useState(false);
  const searchAreaRef = useRef(null);
  const { newArrivalList } = useProducts();
  const router = useRouter();
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const { data = [] } = useFetchData(
    ["SearchProduct", { search }],
    () => SearchProduct({ search }),
    { enabled: !!search }
  );
  const currentUrl = window.location.pathname;

  useEffect(() => {
    handleSearch(routeParams?.search || "");
  }, [routeParams?.search]);

  const timeoutRef = useRef(null); // Use a ref to store the timeout ID

  const handleSearch = (value) => {
    setSearchValue(value);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setSearch(value);
      setSearchShow(value !== "");

      // Update the search query parameter while preserving the current route
      const currentParams = new URLSearchParams(searchParams);
      if (value) {
        currentParams.set("search", value); // Add or update the search parameter
      } else {
        currentParams.delete("search"); // Remove the search parameter if the value is empty
      }

      // Update the URL with the new search parameter while preserving the existing route
      router.push(
        `${window.location.pathname}?${currentParams.toString()}`,
        undefined,
        {
          shallow: true, // Prevents a full page reload
        }
      );
    }, 1000);
  };

  const handleProductClick = (val) => {
    setSearchShow(false); // Close search results
    window.location.href = `/product/${encodeURIComponent(val?.product_name)}`; // Navigate to the product page
    dispatch(setSelectedProductId(val?.id));
  };

  const onRedirect = () => {
    const queryParams = encodeURIComponent(searchValue);
    window.location.href = `/products?search=${queryParams}`;
  };

  const SearchResult = ({ data }) => {
    return !data && searchValue == "" && focus ? (
      <Card className="rounded-none md:rounded">
        {" "}
        <CardHeader>
          <Title size="xs" className="text-midnightIndigo-400 mb-5 p-0">
            Popular searches
          </Title>

          <div className="w-auto flex flex-col md:flex-row justify-start items-start md:items-center gap-x-2.5 gap-3 mt-5 h-[calc(100vh-200px)] md:h-auto">
            {newArrivalList?.slice(0, 3)?.map((val, index) => {
              return (
                <span
                  title={val?.product_name}
                  className="  h-6 px-2 py-1 leading-[150%]  bg-blue-50 rounded text-blue-600 text-sm font-normal cursor-pointer  text-[13px] line-clamp-1 max-w-[250px] text-ellipsis overflow-hidden"
                  onClick={() => {
                    router.push(
                      `/product/${encodeURIComponent(val?.product_name)}`
                    );
                    dispatch(setSelectedProductId(val?.id));
                  }}
                  key={index}
                >
                  {val?.product_name}
                </span>
              );
            })}
          </div>
        </CardHeader>
      </Card>
    ) : (
      focus && (
        <div className="w-full ">
          <Card className="rounded-none md:rounded">
            <CardHeader className="pb-3">
              <Caption className="text-midnightIndigo-400 wireless-Medium text-[14px]">
                {" "}
                {data?.data?.totalItems > 0
                  ? `Search result (${data?.data?.totalItems})`
                  : ""}
              </Caption>
            </CardHeader>

            <CardContent className=" max-h-[calc(100vh-222px)] md:max-h-[400px] w-full overflow-y-auto thin-scrollbar pt-2 transaiton-opacity opacity-100 duration-300">
              {data?.data?.products.length == 0 && (
                <div className="flex gap-2  h-full">
                  <ExclamationCircleIcon className="w-8 h-8 lg:w-6 lg:h-6 mt-[-5px] lg:mt-[0] rotate-180 " />
                  <div className="">
                    <Paragraph className="text-midnightIndigo-400 mb-1">
                      Nothing matches your search
                    </Paragraph>
                    <Caption>
                      But dont't give up-check the spelling or try less specific
                      search terms
                    </Caption>
                  </div>
                </div>
              )}

              {data?.data?.products?.map((ele) => (
                <div
                  key={ele?.id}
                  onClick={() => handleProductClick(ele)}
                  style={{ cursor: "pointer" }}
                >
                  <Product ele={ele} />
                </div>
              ))}
            </CardContent>
            {data?.data?.totalItems > 10 ? (
              <CardFooter className="py-5">
                <div
                  role="button"
                  onClick={() => {
                    onRedirect();
                  }}
                  className="flex items-center text-system-primary hoverunderline hoverunderline-blue gap-1 text-[14px] lg:text-[16px]"
                >
                  View all {data?.data?.totalItems} results
                  <ChevronRight className="w-4 h-4" />
                </div>
              </CardFooter>
            ) : (
              ""
            )}
          </Card>
        </div>
      )
    );
  };
  const hanndleClearSearch = () => {
    setSearch("");
    setSearchValue("");
    setSearchShow(false);
    setFocus(false);
    // Remove the search parameter from the URL while preserving the current route
    const currentParams = new URLSearchParams(searchParams);
    currentParams.delete("search"); // Remove the search parameter

    // Update the URL with the modified parameters while preserving the existing route
    router.push(
      `${window.location.pathname}?${currentParams.toString()}`,
      undefined,
      {
        shallow: true, // Prevents a full page reload
      }
    );
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchAreaRef.current &&
        !searchAreaRef.current.contains(event.target) &&
        search.length === 0
      ) {
        setSearchShow(false);
        setMobileSearchBar(false);
        setFocus(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchShow && currentUrl === "/") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [searchShow]);

  return (
    <div ref={searchAreaRef}>
      <div className="flex items-center justify-center lg:hidden ">
        <Search
          role="buttom"
          onClick={() => setMobileSearchBar(true)}
          className=" w-[20px] h-[20px] text-midnightIndigo-400  md:hidden"
        />
      </div>
      {mobileSearchBar && (
        <div className="flex absolute md:hidden left-0 right-0 top-[50px] z-[99] border px-4 py-2 bg-system-white shadow-sm">
          <div className="relative w-[96%] mx-auto">
            <Input
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              placeholder="Search"
              type="text"
              value={searchValue}
              onFocus={() => setFocus(true)}
              className={`lg:w-[361px] md:w-[266px] pl-[36px] pr-[40px] ${
                focus && "lg:w-[502px]"
              } text-[16px] transition-width duration-300 whitespace-nowrap overflow-hidden text-ellipsis`}
            />
            <Search className="absolute left-[10px] top-[10px] w-[20px] h-[20px] text-midnightIndigo-400" />
            {search?.length > 0 && (
              <X
                role="button"
                onClick={() => {
                  setMobileSearchBar(false);
                  hanndleClearSearch();
                }}
                className="absolute right-[10px] top-[10px] w-[20px] h-[20px] text-midnightIndigo-300 cursor-pointer z-10"
              />
            )}
          </div>
        </div>
      )}
      <div className="relative w-full">
        <div className="hidden md:block md:-m-2 lg:m-0 relative">
          <Input
            onChange={(e) => {
              handleSearch(e.target.value);
            }}
            placeholder="Search"
            type="text"
            autoComplete="off"
            value={searchValue}
            onFocus={() => setFocus(true)}
            className={`lg:w-[385px] md:w-[266px] md:pl-[42px] lg:pl-[36px] pr-[40px] ${
              focus && "lg:w-[526px] md:w-[334px]"
            } text-[16px] transition-width duration-300 whitespace-nowrap overflow-hidden text-ellipsis`}
          />
          <Search className="absolute left-[10px] top-[10px] w-[20px] h-[20px] text-midnightIndigo-400" />
          {search?.length > 0 && (
            <X
              role="button"
              onClick={hanndleClearSearch}
              className="absolute right-[10px] top-[10px] w-[20px] h-[20px] text-midnightIndigo-300 cursor-pointer z-10"
            />
          )}
        </div>

        <div className="hidden md:block  absolute z-[9999]  top-[50px] md:left-[50%] md:translate-x-[-50%] md:w-[414px] lg:w-[526px] ">
          <SearchResult data={data} />
        </div>
      </div>

      <div className=" md:hidden absolute left-0 right-0 top-[110px] md:top-[55px] z-[9999] ">
        <SearchResult data={data} />
      </div>
    </div>
  );
}
